import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const NewsForm = ({ article, onClose }) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    content: '',
    image_url: '',
    status: 'draft',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (article) {
      setFormData({
        title: article?.title || '',
        excerpt: article?.excerpt || '',
        content: article?.content || '',
        image_url: article?.image_url || '',
        status: article?.status || 'draft',
      });
    }
  }, [article]);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');

    if (!formData?.title?.trim()) {
      setError('Titel is verplicht');
      return;
    }

    if (!formData?.content?.trim()) {
      setError('Inhoud is verplicht');
      return;
    }

    try {
      setLoading(true);

      const articleData = {
        ...formData,
        provider_id: user?.id,
        updated_at: new Date()?.toISOString(),
      };

      if (article?.id) {
        // Update existing article
        const { error: updateError } = await supabase?.from('provider_news')?.update(articleData)?.eq('id', article?.id)?.eq('provider_id', user?.id);

        if (updateError) throw updateError;
      } else {
        // Create new article
        const { error: insertError } = await supabase?.from('provider_news')?.insert([articleData]);

        if (insertError) throw insertError;
      }

      onClose();
      window.location?.reload();
    } catch (error) {
      console.error('Error saving article:', error);
      setError('Fout bij opslaan van artikel. Probeer het opnieuw.');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAndPublish = async (e) => {
    e?.preventDefault();
    setFormData({ ...formData, status: 'published' });
    setTimeout(() => handleSubmit(e), 100);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg shadow-elevation-3 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between">
          <h2 className="text-xl font-heading font-bold text-foreground">
            {article ? 'Artikel Bewerken' : 'Nieuw Artikel'}
          </h2>
          <Button
            variant="ghost"
            size="icon"
            iconName="X"
            onClick={onClose}
          />
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-destructive/10 border border-destructive text-destructive px-4 py-3 rounded-md">
              {error}
            </div>
          )}

          {/* Title */}
          <Input
            label="Titel"
            required
            value={formData?.title}
            onChange={(e) => setFormData({ ...formData, title: e?.target?.value })}
            placeholder="Voer een pakkende titel in"
          />

          {/* Excerpt */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">
              Samenvatting
              <span className="text-muted-foreground ml-2">(optioneel)</span>
            </label>
            <textarea
              value={formData?.excerpt}
              onChange={(e) => setFormData({ ...formData, excerpt: e?.target?.value })}
              placeholder="Korte samenvatting voor preview (max 200 karakters)"
              rows={3}
              maxLength={200}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            />
            <p className="text-xs text-muted-foreground">
              {formData?.excerpt?.length || 0}/200 karakters
            </p>
          </div>

          {/* Content */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">
              Inhoud <span className="text-destructive">*</span>
            </label>
            <textarea
              value={formData?.content}
              onChange={(e) => setFormData({ ...formData, content: e?.target?.value })}
              placeholder="Schrijf hier uw artikel inhoud...\n\nU kunt meerdere paragrafen, lijsten en andere content toevoegen."
              rows={12}
              required
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 font-mono"
            />
            <p className="text-xs text-muted-foreground">
              Tip: Gebruik duidelijke paragrafen en koppen voor betere leesbaarheid
            </p>
          </div>

          {/* Image URL */}
          <Input
            label="Afbeelding URL"
            type="url"
            value={formData?.image_url}
            onChange={(e) => setFormData({ ...formData, image_url: e?.target?.value })}
            placeholder="https://example.com/image.jpg"
            description="Voeg een afbeelding toe aan uw artikel (optioneel)"
          />

          {/* Preview */}
          {formData?.image_url && (
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Afbeelding Preview</label>
              <div className="border border-border rounded-lg overflow-hidden">
                <img
                  src={formData?.image_url}
                  alt="Preview"
                  className="w-full h-48 object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-border">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Annuleren
            </Button>
            <Button
              type="submit"
              variant="outline"
              disabled={loading}
              loading={loading}
            >
              Opslaan als Concept
            </Button>
            <Button
              type="button"
              onClick={handleSaveAndPublish}
              disabled={loading}
              loading={loading}
              iconName="Send"
            >
              Opslaan & Publiceren
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewsForm;